import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const CURSOR_EASE = 0.18;
const HOVER_SELECTOR = 'a, button, [data-cursor="hover"]';
const MAGNETIC_SPRING = { type: 'spring', stiffness: 200, damping: 16, mass: 0.4 };
const REVEAL_EASE = [0.22, 0.61, 0.36, 1];
const REVEAL_VIEWPORT = { once: true, margin: '-80px' };
const WORD_VIEWPORT = { once: true, amount: 0.05 };
const WORD_INITIAL = { y: '110%' };
const WORD_ANIMATE = { y: 0 };
const REVEAL_ANIMATE = { opacity: 1, y: 0 };

const getRevealInitial = (y) => ({ opacity: 0, y });
const getRevealTransition = (delay) => ({ duration: 0.9, delay, ease: REVEAL_EASE });
const getWordTransition = (delay, i) => ({ duration: 0.9, delay: delay + i * 0.06, ease: REVEAL_EASE });

export function CustomCursor() {
  const [pos, setPos] = useState({ x: -100, y: -100 });
  const [ringPos, setRingPos] = useState({ x: -100, y: -100 });
  const [hover, setHover] = useState(false);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });

  const handleMove = useCallback((e) => {
    targetRef.current = { x: e.clientX, y: e.clientY };
    setPos({ x: e.clientX, y: e.clientY });
  }, []);

  const handleOver = useCallback((e) => {
    const el = e.target;
    setHover(Boolean(el && el.closest && el.closest(HOVER_SELECTOR)));
  }, []);

  useEffect(() => {
    let rafId;
    const animate = () => {
      currentRef.current.x += (targetRef.current.x - currentRef.current.x) * CURSOR_EASE;
      currentRef.current.y += (targetRef.current.y - currentRef.current.y) * CURSOR_EASE;
      setRingPos({ x: currentRef.current.x, y: currentRef.current.y });
      rafId = requestAnimationFrame(animate);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseover', handleOver);
    rafId = requestAnimationFrame(animate);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseover', handleOver);
      cancelAnimationFrame(rafId);
    };
  }, [handleMove, handleOver]);

  return (
    <>
      <div
        className="cursor-dot"
        style={{ transform: `translate(${pos.x}px, ${pos.y}px) translate(-50%,-50%)` }}
      />
      <div
        className={`cursor-ring ${hover ? 'hover' : ''}`}
        style={{ transform: `translate(${ringPos.x}px, ${ringPos.y}px) translate(-50%,-50%)` }}
      />
    </>
  );
}

export function Magnetic({ children, strength = 0.3, className = '' }) {
  const ref = useRef(null);
  const [delta, setDelta] = useState({ x: 0, y: 0 });

  const onMove = useCallback((e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setDelta({
      x: (e.clientX - (r.left + r.width / 2)) * strength,
      y: (e.clientY - (r.top + r.height / 2)) * strength,
    });
  }, [strength]);

  const onLeave = useCallback(() => setDelta({ x: 0, y: 0 }), []);

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      animate={delta}
      transition={MAGNETIC_SPRING}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Reveal({ children, delay = 0, y = 30, className = '' }) {
  return (
    <motion.div
      initial={getRevealInitial(y)}
      whileInView={REVEAL_ANIMATE}
      viewport={REVEAL_VIEWPORT}
      transition={getRevealTransition(delay)}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function WordReveal({ text, className = '', delay = 0 }) {
  const words = text.split(' ');
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span key={`${text}-${i}-${w}`} className="inline-block overflow-hidden align-bottom mr-[0.25em]">
          <motion.span
            className="inline-block"
            initial={WORD_INITIAL}
            animate={WORD_ANIMATE}
            viewport={WORD_VIEWPORT}
            transition={getWordTransition(delay, i)}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}
